
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, ViewChild, ElementRef, effect, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { NotificationService } from '../../services/notification.service';
import { AppUser } from '../../models/index';
// TranslatePipe removed

@Component({
  selector: 'app-my-profile',
  standalone: true,
  imports: [CommonModule, FormsModule], // Removed TranslatePipe
  templateUrl: './my-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyProfileComponent implements OnInit {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);

  user = signal<AppUser | null>(null);
  isLoading = signal(false);
  isSaving = signal(false);

  // Computed permission: Only Doctors, Admins, Owners, and Platform Admins can sign
  canSign = computed(() => {
    const role = this.user()?.role;
    if (!role) return false;
    // Authorized roles for digital signature
    const allowedRoles = ['doctor', 'admin', 'owner', 'platform_admin'];
    return allowedRoles.includes(role);
  });

  // Signature State
  @ViewChild('signaturePad') signaturePadElement!: ElementRef<HTMLCanvasElement>;
  private canvasContext: CanvasRenderingContext2D | null = null;
  private isDrawing = false;
  signaturePreview = signal<string | null>(null);

  constructor() {
    // Reactively update local state whenever the global user state changes
    effect(() => {
        const currentUser = this.authService.currentUser();
        this.user.set(currentUser);
        
        if (currentUser?.signatureUrl) {
            // Add timestamp to force browser to reload the image (Cache Busting)
            // Check if '?' already exists to append correctly
            const url = currentUser.signatureUrl;
            const separator = url.includes('?') ? '&' : '?';
            const cacheBustedUrl = `${url}${separator}t=${Date.now()}`;
            this.signaturePreview.set(cacheBustedUrl);
        } else {
            this.signaturePreview.set(null);
        }
    }, { allowSignalWrites: true });

    // Initialize Canvas when permission is granted and element is rendered
    // Moved from ngAfterViewInit to fix NG0203
    effect(() => {
        if (this.canSign()) {
            // Wait for DOM update so @if renders the canvas
            setTimeout(() => this.initCanvas(), 100);
        }
    });
  }

  ngOnInit() {
    // Force a fresh fetch from the server to ensure we have the latest signature
    this.authService.reloadCurrentUserProfile();
  }

  initCanvas() {
    if (!this.signaturePadElement) return;
    const canvas = this.signaturePadElement.nativeElement;
    
    // Set canvas resolution to match display size for sharpness
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    this.canvasContext = canvas.getContext('2d');
    if (this.canvasContext) {
      this.canvasContext.lineWidth = 2;
      this.canvasContext.lineCap = 'round';
      this.canvasContext.strokeStyle = 'blue'; // Blue pen
    }

    // Mouse Events
    canvas.addEventListener('mousedown', (e) => this.startDrawing(e));
    canvas.addEventListener('mousemove', (e) => this.draw(e));
    canvas.addEventListener('mouseup', () => this.stopDrawing());
    canvas.addEventListener('mouseout', () => this.stopDrawing());
    
    // Touch Events
    canvas.addEventListener('touchstart', (e) => this.startDrawing(e), { passive: false });
    canvas.addEventListener('touchmove', (e) => this.draw(e), { passive: false });
    canvas.addEventListener('touchend', () => this.stopDrawing());
  }

  startDrawing(event: MouseEvent | TouchEvent) {
    this.isDrawing = true;
    const pos = this.getEventPos(event);
    this.canvasContext?.beginPath();
    this.canvasContext?.moveTo(pos.x, pos.y);
    event.preventDefault();
  }

  draw(event: MouseEvent | TouchEvent) {
    if (!this.isDrawing) return;
    const pos = this.getEventPos(event);
    this.canvasContext?.lineTo(pos.x, pos.y);
    this.canvasContext?.stroke();
    event.preventDefault();
  }

  stopDrawing() {
    this.isDrawing = false;
    this.canvasContext?.closePath();
  }

  getEventPos(event: MouseEvent | TouchEvent) {
    const canvas = this.signaturePadElement.nativeElement;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;

    if ('touches' in event) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = (event as MouseEvent).clientX;
      clientY = (event as MouseEvent).clientY;
    }
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  clearSignature() {
    if (this.signaturePadElement) {
        const canvas = this.signaturePadElement.nativeElement;
        this.canvasContext?.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  async saveSignature() {
    const canvas = this.signaturePadElement.nativeElement;
    
    // Check if empty
    const blank = document.createElement('canvas');
    blank.width = canvas.width;
    blank.height = canvas.height;
    if (canvas.toDataURL() === blank.toDataURL()) {
        this.notificationService.showWarning("กรุณาเซ็นชื่อก่อนบันทึก");
        return;
    }

    const dataUrl = canvas.toDataURL('image/png');
    await this.uploadAndSave(dataUrl);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        this.uploadAndSave(result);
      };
      reader.readAsDataURL(file);
    }
  }

  async uploadAndSave(base64Data: string) {
    const currentUser = this.user();
    if (!currentUser) return;

    this.isSaving.set(true);
    try {
      // 1. Upload to Storage
      const publicUrl = await this.userService.uploadUserSignature(currentUser.uid, base64Data);
      
      // 2. Update User Profile in DB
      const updatedUser: AppUser = {
        ...currentUser,
        signatureUrl: publicUrl
      };
      
      await this.userService.updateUser(updatedUser);
      
      // 3. Update Local State & Global State
      // The effect() will handle the UI update automatically when authService updates
      await this.authService.reloadCurrentUserProfile(); 
      
      this.clearSignature();
      this.notificationService.showSuccess("บันทึกลายเซ็นเรียบร้อยแล้ว");
    } catch (e: any) {
      this.notificationService.showError("เกิดข้อผิดพลาด: " + e.message);
    } finally {
      this.isSaving.set(false);
    }
  }
}
